import { useSyncExternalStore } from 'react';
import type { ThemeMode } from './roleTokenValues';

/**
 * The resolved theme, read from the `dark` class on `<html>`.
 *
 * The class is the single source of truth for the resolved mode on web: the
 * pre-paint bootstrap stamps it and `ThemeManager` keeps it current. Watching
 * it directly keeps shared-ui free of a dependency on shared-logic's
 * preference store while still re-rendering subscribers the moment the mode
 * flips.
 *
 * Imports only from modules without `.web.ts` siblings (see the Metro
 * self-import trap documented in `roleTokens.web.ts`).
 */

// One observer for the whole page, not one per `useThemeMode()` call: this
// hook is called per-row in some lists (see `issueCardCompact.tsx`), and
// `InfiniteDataGrid` doesn't virtualize, so a long list would otherwise
// install one `MutationObserver` on `<html>` per visible row. A single
// module-level observer fans a class-attribute change out to every
// subscriber instead, mirroring native's `themeStore.ts`.
const listeners = new Set<() => void>();
let observer: MutationObserver | undefined;

const ensureObserving = (): void => {
  if (observer || typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return;
  }
  observer = new MutationObserver(() => {
    listeners.forEach((notify) => notify());
  });
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
};

const subscribe = (onChange: () => void): (() => void) => {
  ensureObserving();
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};

const getSnapshot = (): ThemeMode =>
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark')
    ? 'dark'
    : 'light';

export const useThemeMode = (): ThemeMode =>
  useSyncExternalStore(subscribe, getSnapshot, () => 'light');
